import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  appendItems,
  markAllRead,
  markItemRead,
  setTransportDown,
  togglePanel,
} from '../../features/notifications/notificationsSlice';
import {
  ackAllItems as ackAllInsightItems,
  ackItem as ackInsightItem,
  setItems as setInsightItems,
  setTransportDown as setInsightTransportDown,
} from '../../features/insights/insightsSlice';
import {
  ackAllInsights,
  ackInsight,
  getInsightInbox,
  listNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../../utils/api';
import ButtonComponent from '../customButton';
import NotificationRow from './NotificationRow';
import InsightNotificationCard from '../Insights/InsightNotificationCard';
import { INSIGHTS_COPY } from '../Insights/insightsCopy';
import { getNotificationRoute, NOTIFICATION_PANEL_LABELS } from './notification.constants';
import styles from './NotificationPanel.module.scss';

const toInsightPath = (href) => {
  try {
    return new URL(href, window.location.origin).pathname;
  } catch {
    return href;
  }
};

const NotificationPanel = ({ anchorEl }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const drawerRef = useRef(null);
  const { items, nextCursor, panelOpen, status, transportDown, unreadCount } = useSelector((state) => state.notifications);
  const insightItems = useSelector((state) => state.insights.items);
  const insightTransportDown = useSelector((state) => state.insights.transportDown);
  const selectedProjectId = useSelector((state) => state.projects?.selectedProject?.id);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  const closePanel = useCallback(() => {
    if (panelOpen) dispatch(togglePanel());
  }, [dispatch, panelOpen]);

  // Drawer semantics: Escape and backdrop click close it; body scroll is locked while open.
  useEffect(() => {
    if (!panelOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') closePanel();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [panelOpen, closePanel]);

  // Return focus to the trigger button once the drawer finishes closing.
  const wasOpenRef = useRef(panelOpen);
  useEffect(() => {
    if (wasOpenRef.current && !panelOpen) anchorEl?.focus?.();
    wasOpenRef.current = panelOpen;
  }, [panelOpen, anchorEl]);

  // Insights refresh only twice daily server-side (cron), so unlike
  // notifications there's no need to poll — fetch once per drawer open.
  // Independent try/catch from listNotifications (must-fix #6): an insights
  // outage never blocks the notification list, and vice versa. setItems
  // REPLACES the array every open (must-fix #3), so a capability revoked
  // since the last open makes its card disappear on this fetch.
  useEffect(() => {
    if (!panelOpen) return;

    let cancelled = false;
    getInsightInbox({ projectId: selectedProjectId })
      .then((fetchedItems) => {
        if (cancelled) return;
        dispatch(setInsightItems(fetchedItems));
        dispatch(setInsightTransportDown(false));
      })
      .catch(() => {
        if (!cancelled) dispatch(setInsightTransportDown(true));
      });

    return () => {
      cancelled = true;
    };
  }, [panelOpen, dispatch, selectedProjectId]);

  const handleOpenInsight = (item) => {
    if (item.isUnread) {
      ackInsight(item.id, item.fingerprint)
        .then(() => dispatch(ackInsightItem(item.id)))
        .catch(() => dispatch(setInsightTransportDown(true)));
    }

    closePanel();
    navigate(toInsightPath(item.href));
  };

  const handleOpenInsightMember = (item, member) => {
    const href = member.href || member.meta?.href || item.href;
    if (item.isUnread) {
      ackInsight(item.id, item.fingerprint)
        .then(() => dispatch(ackInsightItem(item.id)))
        .catch(() => dispatch(setInsightTransportDown(true)));
    }
    closePanel();
    navigate(toInsightPath(href));
  };

  const handleAckInsight = async (item) => {
    try {
      await ackInsight(item.id, item.fingerprint);
      dispatch(ackInsightItem(item.id));
    } catch {
      dispatch(setInsightTransportDown(true));
    }
  };

  const handleOpen = (item) => {
    const isRead = item?.is_read ?? item?.isRead ?? false;
    if (!isRead) {
      // Non-blocking: navigate even if mark-read fails
      markNotificationAsRead(item.id)
        .then(() => dispatch(markItemRead(item.id)))
        .catch(() => dispatch(setTransportDown(true)));
    }

    closePanel();
    navigate(getNotificationRoute(item));
  };

  const handleMarkRead = async (item) => {
    const isRead = item?.is_read ?? item?.isRead ?? false;
    if (isRead) return;

    try {
      await markNotificationAsRead(item.id);
      dispatch(markItemRead(item.id));
    } catch {
      dispatch(setTransportDown(true));
    }
  };

  const handleMarkAllRead = async () => {
    const hasUnreadInsights = insightItems.some((item) => item.isUnread);
    if ((unreadCount === 0 && !hasUnreadInsights) || isMarkingAllRead) return;

    setIsMarkingAllRead(true);

    const results = await Promise.allSettled([
      unreadCount > 0 ? markAllNotificationsAsRead() : Promise.resolve(),
      hasUnreadInsights ? ackAllInsights({ projectId: selectedProjectId }) : Promise.resolve(),
    ]);

    if (results[0].status === 'fulfilled') {
      dispatch(markAllRead());
    } else {
      dispatch(setTransportDown(true));
    }

    if (results[1].status === 'fulfilled') {
      dispatch(ackAllInsightItems());
    } else {
      dispatch(setInsightTransportDown(true));
    }

    setIsMarkingAllRead(false);
  };

  const loadOlder = async () => {
    if (!nextCursor || isLoadingOlder) return;

    setIsLoadingOlder(true);
    try {
      const response = await listNotifications({ cursor: nextCursor });
      dispatch(appendItems(response));
    } catch {
      dispatch(setTransportDown(true));
    } finally {
      setIsLoadingOlder(false);
    }
  };

  return createPortal(
    <>
      <div
        className={`${styles.backdrop} ${panelOpen ? styles.backdropVisible : ''}`}
        onClick={closePanel}
        aria-hidden="true"
      />
      <section
        ref={drawerRef}
        className={`${styles.drawer} ${panelOpen ? styles.drawerOpen : ''}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!panelOpen}
        aria-label={NOTIFICATION_PANEL_LABELS.heading}
      >
        <header className={styles.header}>
          <h2>{NOTIFICATION_PANEL_LABELS.heading}</h2>
          <div className={styles.headerActions}>
            {(unreadCount > 0 || insightItems.some((item) => item.isUnread)) && (
              <button
                type="button"
                className={styles.markAllRead}
                onClick={handleMarkAllRead}
                disabled={isMarkingAllRead}
              >
                {isMarkingAllRead ? NOTIFICATION_PANEL_LABELS.markingAllRead : NOTIFICATION_PANEL_LABELS.markAllRead}
              </button>
            )}
            <button
              type="button"
              className={styles.closeButton}
              onClick={closePanel}
              aria-label={NOTIFICATION_PANEL_LABELS.close}
            >
              <XMarkIcon className={styles.closeIcon} />
            </button>
          </div>
        </header>
        {transportDown && <p className={styles.transportDown} role="status">{NOTIFICATION_PANEL_LABELS.transportDown}</p>}
        {insightTransportDown && <p className={styles.transportDown} role="status">{INSIGHTS_COPY.loadError}</p>}
        {insightItems.length > 0 && (
          <div className={styles.insightsSection}>
            <h3 className={styles.sectionHeading}>{INSIGHTS_COPY.heading}</h3>
            {insightItems.map((item) => (
              <InsightNotificationCard
                key={item.id}
                item={item}
                onOpen={handleOpenInsight}
                onOpenMember={handleOpenInsightMember}
                onAck={handleAckInsight}
              />
            ))}
          </div>
        )}
        <div className={styles.list}>
          {items.length === 0 ? (
            <p className={styles.empty}>
              {status === 'loading' ? NOTIFICATION_PANEL_LABELS.loading : NOTIFICATION_PANEL_LABELS.empty}
            </p>
          ) : (
            items.map((item) => (
              <NotificationRow
                key={item.id}
                item={item}
                onOpen={handleOpen}
                onMarkRead={handleMarkRead}
              />
            ))
          )}
        </div>
        {nextCursor && (
          <div className={styles.footer}>
            <ButtonComponent
              ctaText={isLoadingOlder ? NOTIFICATION_PANEL_LABELS.loading : NOTIFICATION_PANEL_LABELS.loadOlder}
              variant="secondary"
              size="small"
              className={styles.loadOlder}
              onClick={loadOlder}
              disabled={isLoadingOlder}
              loading={isLoadingOlder}
            />
          </div>
        )}
      </section>
    </>,
    document.body,
  );
};

export default NotificationPanel;
